import CircularProgress from '@material-ui/core/CircularProgress'
import { uniqueId } from 'lodash'
import { forwardRef, useCallback, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'

import { HandlerOf } from '../../helpers/typeHelper'
import { mergeRefs } from '../../helpers/utils'
import { darkGray, royalBlue } from '../../theme/colors'
import { FlexBox } from '../Base/FlexBox'
import { Box } from '../Box/Box'
import { Text } from '../Text/Text'
import {
  acceptedFiles,
  cloneFile,
  fileOnLoad,
  getLimitInMegabytes,
  getUploadedFilename,
} from './DropZoneArea'
import {
  CancelFile,
  DescriptionIcon,
  DropZoneContainer,
  DropZoneWrap,
  ErrorMessage,
  FileInput,
  FileName,
  FileNameWrapper,
  FilesList,
  OnDrop,
  UploadIcon,
} from './DropZoneArea.styles'

export interface DropZoneMultipleAreaProps {
  onChange?: HandlerOf<FileWithUUID[] | undefined>
  limit?: number
  hasError?: boolean
  noMargin?: boolean
  label?: React.ReactNode
  uploadedFiles?: string[]
}

interface FileWithUUID extends File {
  uuid?: string
}

const StyledDescriptionIcon = styled(DescriptionIcon)`
  font-size: 18px;
`

const StyledCancelFile = styled(CancelFile)`
  font-size: 18px;
`

export const DropZoneMultipleArea = forwardRef(
  (
    { onChange, limit = 30, noMargin, label, uploadedFiles = [], ...restProps }: DropZoneMultipleAreaProps,
    ref
  ) => {
    const fileUploader = useRef<HTMLInputElement>(null)
    const [dragStatus, setDragStatus] = useState('leave')
    const [files, setFiles] = useState<FileWithUUID[]>([])
    const [hasError, setHasError] = useState(restProps.hasError)
    const [isLoadingFile, setIsLoadingFile] = useState(false)

    const validateFile = (file: FileWithUUID) =>
      new Promise(async (resolve, reject) => {
        if (!file) {
          setHasError(true)
          setIsLoadingFile(false)
          reject()
        }
        if (file.size > getLimitInMegabytes(limit)) {
          setHasError(true)
          setIsLoadingFile(false)
          reject()
        } else if (!acceptedFiles.includes(file.type)) {
          setHasError(true)
          setIsLoadingFile(false)
          reject()
        } else {
          setHasError(false)
          await fileOnLoad(file)
          setIsLoadingFile(false)
          // So we can have a uniq file if user decide to upload same file more than once
          file.uuid = uniqueId()
          resolve(file)
        }
      })

    const validateFiles = async (addedFiles: FileWithUUID[]) => {
      if (!addedFiles?.length) onChange?.([])
      await Promise.all(addedFiles.map(validateFile))
      const newFiles = [...addedFiles, ...files]
      const newClonedFiles = await Promise.all(newFiles.map(cloneFile))
      setFiles(newClonedFiles)
      onChange?.(newClonedFiles)
    }

    const handleFileClickUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
      e.stopPropagation()
      setIsLoadingFile(true)
      validateFiles(Array.from(e.target?.files || []))
    }

    const handleFileDrop = (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault()
      e.stopPropagation()

      setDragStatus('leave')
      setIsLoadingFile(true)
      validateFiles(Array.from(e.dataTransfer?.files || []))
    }

    const handleDragStatus = useCallback((status: string) => {
      setDragStatus(status)
    }, [])

    const removeFile = (file: FileWithUUID) => {
      const filteredFiles = files.filter((f) => f !== file)
      setFiles(filteredFiles)
      onChange?.(filteredFiles)
    }

    const handleOnDragOver = (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault()
    }

    const handleClick = () => {
      fileUploader.current?.click()
    }

    useEffect(() => {
      setHasError(restProps.hasError)
    }, [restProps.hasError])

    function maybeRenderFileList() {
      if (files.length === 0 && uploadedFiles?.length === 0) return null

      return (
        <FilesList>
          {files.map((file) => (
            <FileNameWrapper key={`${file.uuid || file.name}`} $hasHover>
              <StyledDescriptionIcon />
              <FileName>
                <Text variant="paragraph2" fontColor={darkGray} styles="margin-top: 3px;">
                  {file.name}
                </Text>
              </FileName>
              <StyledCancelFile onClick={() => removeFile(file)} />
            </FileNameWrapper>
          ))}
          {uploadedFiles?.map((file) => (
            <FileNameWrapper key={file} $hasHover>
              <StyledDescriptionIcon />
              <FileName>
                <Text variant="paragraph2" fontColor={darkGray} styles="margin-top: 3px;">
                  {getUploadedFilename(file)}
                </Text>
              </FileName>
            </FileNameWrapper>
          ))}
        </FilesList>
      )
    }

    return (
      <DropZoneWrap>
        {label && (
          <>
            <Text variant="label4" fontColor="black">
              {label}
            </Text>
            <Box m={0.5} />
          </>
        )}
        {maybeRenderFileList()}
        <DropZoneContainer
          $hasMultipleFileList={files.length > 0}
          $noMargin={noMargin}
          $hasError={hasError}
          $dragStatus={dragStatus === 'enter'}
          onClick={handleClick}
          onDragOver={handleOnDragOver}
          onDragEnter={() => handleDragStatus('enter')}
          onDragLeave={() => handleDragStatus('leave')}
          onDrop={handleFileDrop}
          data-testid="dropzone"
        >
          <FileInput ref={mergeRefs([fileUploader, ref])} onChange={handleFileClickUpload} multiple />
          {dragStatus === 'leave' && (
            <FlexBox gap="5px">
              <UploadIcon />
              <Text variant="header3" color={royalBlue}>
                Upload
              </Text>
            </FlexBox>
          )}
          {dragStatus === 'enter' && (
            <OnDrop>
              <UploadIcon />
              <Text variant="header3" color={royalBlue}>
                Drop the files
              </Text>
            </OnDrop>
          )}
          {isLoadingFile && <CircularProgress color="primary" />}
        </DropZoneContainer>
        {hasError && (
          <ErrorMessage>Upload a supported file format ({limit} MB max – jpg, png, gif, pdf).</ErrorMessage>
        )}
      </DropZoneWrap>
    )
  }
)
